/**
 * AI Cost Calculator — public lead-magnet
 * ========================================
 * Visitor pastes their monthly LLM bill → instantly sees per-feature breakdown
 * + waste estimate + savings preview. Strong-signal CTA at the bottom drives
 * sign-up. Built for HN / Twitter / direct-share top-of-funnel.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, TrendingDown, ArrowRight, Loader2, Calculator, Zap } from 'lucide-react';
import { ENGINE_COUNT } from '../constants/product';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const USE_CASES = [
  { id: 'rag', label: 'RAG / Q&A' },
  { id: 'chat', label: 'Customer chat' },
  { id: 'agent', label: 'AI agent' },
  { id: 'completion', label: 'Code completion' },
  { id: 'embedding', label: 'Embeddings / search' },
  { id: 'mixed', label: 'Mixed' },
];

const PROVIDERS = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'mixed', label: 'Multiple' },
];

const AICostCalculator = () => {
  // v32: "Predictive Mode" — for teams who haven't launched yet and don't have a bill yet.
  // Per Feb 24 user audit: captures pre-launch users who are terrified of the upcoming bill.
  const [mode, setMode] = useState('actual'); // 'actual' | 'predictive'
  const [monthlyBill, setMonthlyBill] = useState(18500);
  const [provider, setProvider] = useState('openai');
  const [useCase, setUseCase] = useState('rag');
  const [customers, setCustomers] = useState(850);
  // Predictive-mode inputs:
  const [predModel, setPredModel] = useState('gpt-4o');
  const [predRequestsPerMo, setPredRequestsPerMo] = useState(1_000_000);
  const [predAvgInputTokens, setPredAvgInputTokens] = useState(800);
  const [predAvgOutputTokens, setPredAvgOutputTokens] = useState(200);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);

  // Public pricing per 1M tokens — updated Feb 24, 2026.
  // Used ONLY to compute a predictive baseline so the existing /calculate
  // endpoint sees a valid `monthly_bill_usd`. Customer never sees these.
  const PRED_PRICING = {
    'gpt-4o':       { in: 2.50, out: 10.00 },
    'gpt-4o-mini':  { in: 0.15, out: 0.60 },
    'gpt-5.2':      { in: 5.00, out: 15.00 },
    'claude-sonnet-4.5': { in: 3.00, out: 15.00 },
    'claude-haiku-4.5':  { in: 0.80, out: 4.00 },
    'gemini-3-pro':   { in: 1.25, out: 5.00 },
    'gemini-3-flash': { in: 0.075, out: 0.30 },
  };

  const predictedBill = (() => {
    const p = PRED_PRICING[predModel] || PRED_PRICING['gpt-4o'];
    const inCost  = (predRequestsPerMo * predAvgInputTokens  / 1_000_000) * p.in;
    const outCost = (predRequestsPerMo * predAvgOutputTokens / 1_000_000) * p.out;
    return Math.round(inCost + outCost);
  })();

  useEffect(() => {
    axios.get(`${API_URL}/ai-cost-calculator/sample-bills`).then(r => setSamples(r.data.samples || []));
    axios.get(`${API_URL}/ai-cost-calculator/benchmarks`).then(r => setBenchmarks(r.data)).catch(() => {});
  }, []);

  const calculate = async () => {
    setLoading(true);
    try {
      // In Predictive mode, swap the actual bill with our computed estimate so
      // the rest of the engine ROI math still applies. We also infer provider
      // from the chosen model (openai/anthropic/gemini).
      const effectiveBill = mode === 'predictive' ? predictedBill : Number(monthlyBill);
      const effectiveProvider = mode === 'predictive'
        ? (predModel.startsWith('gpt-') ? 'openai' : predModel.startsWith('claude-') ? 'anthropic' : 'gemini')
        : provider;
      const r = await axios.post(`${API_URL}/ai-cost-calculator/calculate`, {
        monthly_bill_usd: effectiveBill,
        provider: effectiveProvider,
        primary_use_case: useCase,
        customers_served: customers ? Number(customers) : null,
      });
      setResult(r.data);
      setTimeout(() => {
        document.getElementById('result-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const applySample = (s) => {
    setMonthlyBill(s.monthly_bill_usd);
    setProvider(s.provider);
    setUseCase(s.primary_use_case);
    setCustomers(s.customers_served);
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="ai-cost-calculator-page">
      <Helmet>
        <title>AI Cost Calculator · Find your LLM waste in 10 seconds | CARTIEAI</title>
        <meta name="description" content="Paste your OpenAI / Anthropic / Gemini / Groq / Mistral bill. See your per-feature P&L preview + how much CARTIE's Smart Engines would save you. Free, no signup required." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        {/* HERO */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 dark:text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 mb-5 bg-emerald-500/10 dark:bg-emerald-500/5">
            <Calculator className="w-3 h-3" /> Free public tool · No signup required
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
            What's <span className="text-emerald-400 dark:text-emerald-400">wasted</span><br />in your LLM bill?
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed">
            Most companies waste <strong>26-41%</strong> of their AI spend on overpriced models, sync calls that should be batch, and prompts that bust the provider cache.
            Paste your monthly bill below — see your per-feature breakdown + how much CARTIE's Smart Engines would save in 10 seconds.
          </p>
        </div>

        {/* CALCULATOR FORM */}
        <Card className="bg-slate-900/60 border-emerald-500/40 max-w-3xl shadow-lg" data-testid="aicc-form">
          <CardHeader>
            <CardTitle className="text-white text-xl flex items-center gap-2">
              <Zap className="w-5 h-5 text-emerald-400 dark:text-emerald-400" /> Your LLM bill
            </CardTitle>
            <CardDescription className="text-slate-300">No data leaves your browser. Industry averages used for the preview.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Mode toggle (v32) — let pre-launch teams estimate without a bill */}
            <div
              role="tablist"
              aria-label="Calculation mode"
              className="inline-flex p-1 rounded-lg bg-slate-900 border border-white/10"
              data-testid="aicc-mode-toggle"
            >
              {[
                { id: 'actual', label: 'Bill-based', desc: 'Paste my monthly LLM spend' },
                { id: 'predictive', label: 'Predictive Mode', desc: 'Estimate from expected traffic (no bill needed)' },
              ].map((t) => {
                const active = mode === t.id;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => { setMode(t.id); setResult(null); }}
                    className={`text-xs px-3 py-1.5 rounded-md transition-all ${
                      active
                        ? 'bg-emerald-500 text-slate-950 font-bold shadow-sm shadow-emerald-500/30'
                        : 'text-slate-300 hover:text-white'
                    }`}
                    data-testid={`aicc-mode-${t.id}`}
                    title={t.desc}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {mode === 'actual' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="aicc-bill" className="text-slate-200 text-sm">Monthly bill (USD)</Label>
                <Input
                  id="aicc-bill"
                  type="number"
                  value={monthlyBill}
                  onChange={(e) => setMonthlyBill(e.target.value)}
                  className="bg-slate-900 dark:bg-slate-950 border-emerald-500/30 dark:border-white/10 text-white font-mono mt-1"
                  data-testid="aicc-bill-input"
                  placeholder="18500"
                />
              </div>
              <div>
                <Label htmlFor="aicc-customers" className="text-slate-200 text-sm">Customers served (optional)</Label>
                <Input
                  id="aicc-customers"
                  type="number"
                  value={customers}
                  onChange={(e) => setCustomers(e.target.value)}
                  className="bg-slate-900 dark:bg-slate-950 border-emerald-500/30 dark:border-white/10 text-white font-mono mt-1"
                  data-testid="aicc-customers-input"
                  placeholder="850"
                />
              </div>
            </div>
            ) : (
            /* Predictive mode inputs — v32 */
            <div className="space-y-3" data-testid="aicc-predictive-block">
              <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold">Predictive estimator · pre-launch teams</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-200 text-sm">Model</Label>
                  <select
                    value={predModel}
                    onChange={(e) => setPredModel(e.target.value)}
                    className="w-full bg-slate-900 border border-emerald-500/30 text-white font-mono mt-1 rounded-md p-2 text-sm"
                    data-testid="aicc-pred-model"
                  >
                    <optgroup label="OpenAI">
                      <option value="gpt-5.2">GPT-5.2</option>
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-4o-mini">GPT-4o mini</option>
                    </optgroup>
                    <optgroup label="Anthropic">
                      <option value="claude-sonnet-4.5">Claude Sonnet 4.5</option>
                      <option value="claude-haiku-4.5">Claude Haiku 4.5</option>
                    </optgroup>
                    <optgroup label="Google">
                      <option value="gemini-3-pro">Gemini 3 Pro</option>
                      <option value="gemini-3-flash">Gemini 3 Flash</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <Label htmlFor="aicc-req" className="text-slate-200 text-sm">Requests/month</Label>
                  <Input
                    id="aicc-req"
                    type="number"
                    value={predRequestsPerMo}
                    onChange={(e) => setPredRequestsPerMo(Number(e.target.value) || 0)}
                    className="bg-slate-900 border-emerald-500/30 text-white font-mono mt-1"
                    data-testid="aicc-pred-requests"
                    placeholder="1000000"
                  />
                </div>
                <div>
                  <Label htmlFor="aicc-in" className="text-slate-200 text-sm">Avg input tokens / request</Label>
                  <Input
                    id="aicc-in"
                    type="number"
                    value={predAvgInputTokens}
                    onChange={(e) => setPredAvgInputTokens(Number(e.target.value) || 0)}
                    className="bg-slate-900 border-emerald-500/30 text-white font-mono mt-1"
                    data-testid="aicc-pred-input-tokens"
                    placeholder="800"
                  />
                </div>
                <div>
                  <Label htmlFor="aicc-out" className="text-slate-200 text-sm">Avg output tokens / request</Label>
                  <Input
                    id="aicc-out"
                    type="number"
                    value={predAvgOutputTokens}
                    onChange={(e) => setPredAvgOutputTokens(Number(e.target.value) || 0)}
                    className="bg-slate-900 border-emerald-500/30 text-white font-mono mt-1"
                    data-testid="aicc-pred-output-tokens"
                    placeholder="200"
                  />
                </div>
              </div>
              {/* Live predicted bill estimate */}
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="aicc-pred-estimate">
                <div>
                  <div className="text-[10px] text-emerald-300 uppercase tracking-widest font-bold">Predicted monthly bill</div>
                  <div className="text-xs text-slate-400 mt-0.5">Public pricing × your inputs. Updated Feb 2026.</div>
                </div>
                <div className="text-3xl font-extrabold text-emerald-300 tabular-nums">{fmt(predictedBill)}</div>
              </div>
            </div>
            )}

            {mode === 'actual' && (
            <div>
              <Label className="text-slate-200 text-sm">Provider</Label>
              <div className="flex flex-wrap gap-2 mt-1.5" data-testid="aicc-providers">
                {PROVIDERS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setProvider(p.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${provider === p.id ? 'bg-emerald-500 border-emerald-600 text-white' : 'bg-slate-900 dark:bg-slate-950 border-emerald-500/30 dark:border-white/10 text-slate-200 hover:border-emerald-500/60'}`}
                    data-testid={`aicc-provider-${p.id}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            )}

            <div>
              <Label className="text-slate-200 text-sm">Primary use-case</Label>
              <div className="flex flex-wrap gap-2 mt-1.5" data-testid="aicc-use-cases">
                {USE_CASES.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setUseCase(u.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${useCase === u.id ? 'bg-emerald-500 border-emerald-600 text-white' : 'bg-slate-900 dark:bg-slate-950 border-emerald-500/30 dark:border-white/10 text-slate-200 hover:border-emerald-500/60'}`}
                    data-testid={`aicc-use-${u.id}`}
                  >
                    {u.label}
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={calculate} disabled={loading || (mode === 'actual' ? !monthlyBill : !predRequestsPerMo)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold w-full sm:w-auto shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5" data-testid="aicc-calc-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
              {mode === 'predictive' ? `Estimate waste on ${fmt(predictedBill)}/mo` : 'Calculate my waste'}
            </Button>

            {samples.length > 0 && (
              <div className="pt-3 border-t border-emerald-500/20 dark:border-white/5">
                <div className="text-[10px] uppercase tracking-widest text-slate-300 mb-2">Try a sample:</div>
                <div className="flex flex-wrap gap-2">
                  {samples.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => applySample(s)}
                      className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/10 dark:bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 dark:text-emerald-300 hover:bg-emerald-500/20 dark:hover:bg-emerald-500/20"
                      data-testid={`aicc-sample-${i}`}
                    >
                      {s.name} · {fmt(s.monthly_bill_usd)}/mo
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* RESULTS */}
        {result && (
          <div id="result-section" className="mt-12 space-y-6" data-testid="aicc-result">
            {/* Headline number */}
            <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-500/15 dark:to-teal-500/5 border-emerald-500/40 shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 dark:text-emerald-300 font-semibold mb-2">YOU'RE LIKELY WASTING</div>
                <div className="text-5xl sm:text-6xl font-bold text-emerald-400 dark:text-emerald-400" data-testid="aicc-waste">
                  {fmt(result.savings_with_cartie.estimated_waste_usd)}
                </div>
                <div className="text-base text-slate-200 mt-2">
                  / month — that's <strong>{result.savings_with_cartie.estimated_waste_pct}%</strong> of your {fmt(result.input.monthly_bill_usd)} bill
                </div>
                <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 dark:bg-emerald-500/20 border border-emerald-500/40">
                  <TrendingDown className="w-4 h-4 text-emerald-400 dark:text-emerald-400" />
                  <span className="text-sm text-white font-semibold">CARTIE recovers <span className="text-emerald-400 dark:text-emerald-400">{fmt(result.savings_with_cartie.total_monthly_save_usd)}/mo</span> ({result.savings_with_cartie.total_monthly_save_pct}%) — <span className="text-emerald-400 dark:text-emerald-400">{fmt(result.savings_with_cartie.annualized_save_usd)}/yr</span></span>
                </div>
              </CardContent>
            </Card>

            {/* Top 3 features */}
            <Card className="bg-slate-900/40 border-emerald-500/20 dark:border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg">Where your money is going</CardTitle>
                <CardDescription className="text-slate-300">Top 3 most expensive features. CARTIE per-feature P&L gives you this for your real workload.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="aicc-features">
                  {result.top_3_expensive_features.map((f, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="text-2xl font-bold text-emerald-400 dark:text-emerald-400 w-8">#{i + 1}</div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-white">{f.name}</div>
                        <div className="text-xs text-slate-300">{f.share_pct}% of bill</div>
                      </div>
                      <div className="text-base font-bold text-white font-mono">{fmt(f.monthly_cost_usd)}</div>
                    </div>
                  ))}
                </div>
                {result.per_customer_unit_cost_usd !== null && (
                  <div className="mt-5 p-3 rounded-md bg-emerald-500/10 dark:bg-emerald-500/5 border border-emerald-500/20">
                    <div className="text-xs text-slate-200">
                      <strong>Per-customer unit cost:</strong> <span className="text-emerald-300 dark:text-emerald-300 font-mono">${result.per_customer_unit_cost_usd}</span> / customer / month
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Engines breakdown */}
            <Card className="bg-slate-900/40 border-emerald-500/30">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-400 dark:text-emerald-400" /> How CARTIE recovers it
                </CardTitle>
                <CardDescription className="text-slate-300">4 Smart Engines that target each waste category.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="aicc-engines">
                  {result.savings_with_cartie.engines.map((e, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-md border border-emerald-500/20 dark:border-white/5 bg-slate-900 dark:bg-slate-950/40">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-white">{e.engine}</div>
                        <div className="text-xs text-slate-300 mt-1">{e.what_it_does}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-base font-bold text-emerald-400 dark:text-emerald-400 font-mono">{fmt(e.saves_usd)}</div>
                        <div className="text-[10px] text-slate-300">{e.saves_pct}% saved</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="bg-emerald-500 bg-gradient-to-br from-emerald-500 to-emerald-600 border-emerald-700 shadow-xl">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl sm:text-3xl font-bold text-white">{result.next_step.title}</h3>
                <p className="text-emerald-50 mt-3 max-w-xl mx-auto text-base">
                  {result.next_step.subtitle}
                </p>
                <Link
                  to={result.next_step.cta_path}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-emerald-500/10 text-emerald-300 font-semibold text-sm transition-all"
                  data-testid="aicc-cta-btn"
                >
                  {result.next_step.cta_label} <ArrowRight className="w-4 h-4" />
                </Link>
                <div className="mt-3 text-xs text-emerald-100">
                  See your <strong>real</strong> per-feature P&amp;L in 30 seconds. No credit card.
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* PUBLIC BENCHMARKS — Network-effect corpus */}
        {benchmarks && (
          <Card className="mt-12 bg-slate-900/40 border-emerald-500/40 max-w-5xl" data-testid="aicc-benchmarks">
            <CardHeader>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-300 dark:text-emerald-300 font-mono mb-2">
                <Sparkles className="w-3 h-3" /> {benchmarks.headline.is_real_majority
                  ? `Network-effect benchmarks · ${benchmarks.headline.real_submissions.toLocaleString()} real bills`
                  : `Engine modeling · ${(benchmarks.headline.simulated_submissions || 0).toLocaleString()} patterns + ${benchmarks.headline.real_submissions.toLocaleString()} real bills`}
              </div>
              <CardTitle className="text-white text-xl">
                {benchmarks.headline.headline_quote}
              </CardTitle>
              <CardDescription className="text-slate-300">
                Public, anonymized corpus from every calculator submission. We never store your email or company name. Use the calculator above to contribute.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div data-testid="aicc-by-usecase">
                  <div className="text-[10px] uppercase tracking-widest text-slate-300 font-mono mb-2">Average waste by use-case</div>
                  <div className="space-y-1.5">
                    {benchmarks.by_use_case.map((u) => (
                      <div key={u.use_case} className="flex items-center gap-2">
                        <div className="text-xs text-slate-200 w-24 capitalize">{u.use_case}</div>
                        <div className="flex-1 h-2 rounded bg-slate-200 dark:bg-white/5 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, u.avg_waste_pct * 2)}%` }} />
                        </div>
                        <div className="text-xs font-mono font-semibold text-white w-12 text-right">{u.avg_waste_pct}%</div>
                        <div className="text-[10px] text-slate-300 w-12 text-right">n={u.submissions}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div data-testid="aicc-by-band">
                  <div className="text-[10px] uppercase tracking-widest text-slate-300 font-mono mb-2">Average waste by bill size</div>
                  <div className="space-y-1.5">
                    {benchmarks.by_bill_band.map((b) => (
                      <div key={b.band} className="flex items-center gap-2">
                        <div className="text-xs text-slate-200 w-24">{b.band}</div>
                        <div className="flex-1 h-2 rounded bg-slate-200 dark:bg-white/5 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, b.avg_waste_pct * 2)}%` }} />
                        </div>
                        <div className="text-xs font-mono font-semibold text-white w-12 text-right">{b.avg_waste_pct}%</div>
                        <div className="text-[10px] text-slate-300 w-12 text-right">n={b.submissions}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-emerald-500/20 dark:border-white/5 text-[10px] text-slate-300">
                {benchmarks.disclaimer}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Forward navigation — clear next-step CTAs (Feb 24 2026) */}
        <div className="mt-12 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-8 shadow-md shadow-emerald-500/10" data-testid="aicc-next-steps">
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Like what you saw?</h3>
          <p className="text-slate-300 mt-2 mb-6 max-w-2xl">
            This is just one of <span className="text-emerald-300 font-semibold">{ENGINE_COUNT} engines</span> CARTIEAI ships.
            Start a 14-day pilot — no card, no commitment — and let us measure your actual cost-recoverable waste.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/pricing">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 w-full sm:w-auto shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5" data-testid="aicc-cta-pilot">
                Start free pilot
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/tools/finops-maturity">
              <Button size="lg" variant="outline" className="border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="aicc-cta-reality">
                Take the FinOps Reality Check
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white/10 bg-transparent text-slate-400 hover:bg-white/5 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="aicc-cta-home">
                ← Back to home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICostCalculator;
